/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Linking,
  Platform,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing';
import { settingsStyles } from '../../settings/screens/settingsScreen.styles';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { showInfo, showSuccess } from '../../shared/ui/appToast';
import { saleDetailStyles as styles } from '../../sales/components/saleDetail.styles';
import { formatInvoiceCaeLine } from '../../sales/application/salePresentation';
import { resolveFiscalEmissionUi } from '../domain/fiscalEmissionPresentation';
import { FISCAL_SYSTEM_COPY } from '../domain/fiscalSystemCopy';
import { createEmptyFiscalBuyerDraft, type PosFiscalBuyerDraft } from '../domain/fiscalTypes';
import { normalizeCuitDigits } from '../domain/fiscalPresentation';
import { PosFiscalBuyerSection } from './PosFiscalBuyerSection';
import { formatMoney } from '../../shared/format/public-api';
import {
  consultFiscalInvoiceInAfip,
  emitFiscalDebitNote,
  fetchCreditNoteForSale,
  fetchDebitNotesForSale,
  fetchFiscalInvoiceForSale,
  retryFiscalCreditNoteEmit,
  retryFiscalInvoiceEmit,
  updateSaleFiscalBuyerForReemit,
} from '../infra/fiscalRepository'
import type { FiscalInvoiceConsultResult, FiscalInvoiceDetailRow, FiscalInvoiceEmitResult } from '../infra/fiscalRepository'
import { copyFiscalComprobanteSummary, downloadFiscalComprobantePdf } from '../application/fiscalInvoiceExport'
import type { FiscalComprobanteExportInput } from '../application/fiscalInvoiceExport'
import { buildAfipQrUrl, formatComprobanteNumber } from '../application/fiscalComprobanteFormat';
import { resolveInvoiceType, spanishInvoiceTypeLabel } from '../domain/invoiceTypes';
import { getFiscalContext } from '../infra/fiscalConfigRepo';
import {
  fiscalSalePaymentConditionLabel,
  printFiscalComprobante,
} from '../../printing/public-api';
import { fetchSalePickupTrackingUrl } from '../../printing/infra/pickupTrackingRepo';
import { roundMoney } from '@shared/money';
import { useOrgMoneyScale } from '../../shared/money/orgMoneyScale';

export type SaleFiscalComprobantePanelProps = {
  saleId: string;
  branchId: string;
  fiscalMode: string;
  fiscalStatus: string;
  saleStatus?: string;
  invoiceStatus?: string | null;
  invoiceCae?: string | null;
  invoiceIssuedAt?: string | null;
  invoiceLastError?: string | null;
  invoiceQrUrl?: string | null;
  exportContext?: FiscalComprobanteExportInput | null;
  emitterFiscalEnvironment?: string | null;
  creditNoteStatus?: string | null;
  creditNoteCae?: string | null;
  creditNoteIssuedAt?: string | null;
  canRetry?: boolean;
  onRetryComplete?: () => void;
};

function formatConsultSummary(result: FiscalInvoiceConsultResult): string {
  const lines = [
    `Ambiente: ${result.environment ?? '—'}`,
    `CAE ARCA: ${result.afip?.cae ?? '—'}`,
    `CAE Vuelto: ${result.stored?.cae ?? '—'}`,
    `Match CAE: ${result.matches?.cae === true ? 'Sí' : result.matches?.cae === false ? 'No' : '—'}`,
    `Importe ARCA: ${result.afip?.impTotal ?? '—'}`,
    `Importe Vuelto: ${result.stored?.totalAmount ?? '—'}`,
    `Match importe: ${result.matches?.importe === true ? 'Sí' : result.matches?.importe === false ? 'No' : '—'}`,
  ];
  return lines.join('\n');
}

export function SaleFiscalComprobantePanel({
  saleId,
  branchId,
  fiscalMode,
  fiscalStatus,
  saleStatus,
  invoiceStatus,
  invoiceCae,
  invoiceIssuedAt,
  invoiceLastError,
  invoiceQrUrl,
  exportContext,
  canRetry = true,
  emitterFiscalEnvironment,
  creditNoteStatus,
  creditNoteCae,
  creditNoteIssuedAt,
  onRetryComplete,
}: SaleFiscalComprobantePanelProps) {
  const moneyScale = useOrgMoneyScale();
  const [retrying, setRetrying] = useState(false);
  const [consulting, setConsulting] = useState(false);
  const [ncRetrying, setNcRetrying] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [thermalPrinting, setThermalPrinting] = useState(false);
  const [ncPrinting, setNcPrinting] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [emitOverride, setEmitOverride] = useState<FiscalInvoiceEmitResult | null>(null);
  const [ncCaeOverride, setNcCaeOverride] = useState<string | null>(null);
  const [debitNotes, setDebitNotes] = useState<FiscalInvoiceDetailRow[]>([]);
  const [ndFormOpen, setNdFormOpen] = useState(false);
  const [ndAmountText, setNdAmountText] = useState('');
  const [ndEmitting, setNdEmitting] = useState(false);
  const [ndError, setNdError] = useState<string | null>(null);
  const [ndPrintingId, setNdPrintingId] = useState<string | null>(null);
  const [buyerEditOpen, setBuyerEditOpen] = useState(false);
  const [buyerDraft, setBuyerDraft] = useState<PosFiscalBuyerDraft>(() => createEmptyFiscalBuyerDraft());
  const [buyerSaving, setBuyerSaving] = useState(false);

  useEffect(() => {
    setEmitOverride(null);
    setNcCaeOverride(null);
  }, [saleId, fiscalStatus, invoiceStatus, invoiceCae, invoiceIssuedAt, invoiceQrUrl, creditNoteCae]);

  const effectiveFiscalStatus =
    emitOverride?.status === 'approved' || emitOverride?.cae?.trim()
      ? 'approved'
      : fiscalStatus;
  const effectiveInvoiceStatus =
    emitOverride?.status === 'approved' || emitOverride?.cae?.trim()
      ? 'approved'
      : invoiceStatus;
  const effectiveInvoiceCae = emitOverride?.cae?.trim() || invoiceCae;
  const effectiveInvoiceIssuedAt = emitOverride?.issuedAt ?? invoiceIssuedAt;
  const effectiveInvoiceQrUrl = emitOverride?.qrUrl ?? invoiceQrUrl;
  const effectiveCreditNoteCae = ncCaeOverride ?? creditNoteCae;

  const canEditBuyer =
    saleStatus !== 'voided' && !effectiveInvoiceCae?.trim() && fiscalMode !== 'non_fiscal';

  useEffect(() => {
    const cuit = exportContext?.buyerCuit?.trim() ?? '';
    const consumidorFinal = !cuit;
    setBuyerDraft({
      consumidorFinal,
      cuit: cuit || '',
      razonSocial: exportContext?.buyerName?.trim() || (consumidorFinal ? 'Consumidor final' : ''),
      taxCondition: null,
      linkedCustomerId: null,
    });
    setBuyerEditOpen(false);
  }, [saleId, exportContext?.buyerCuit, exportContext?.buyerName]);

  const canEmitDebitNote =
    canRetry && saleStatus !== 'voided' && Boolean(effectiveInvoiceCae?.trim());

  const reloadDebitNotes = async () => {
    const rows = await fetchDebitNotesForSale(saleId);
    setDebitNotes(rows);
  };

  useEffect(() => {
    if (canEmitDebitNote) void reloadDebitNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleId, canEmitDebitNote]);

  const exportInput: FiscalComprobanteExportInput | null =
    exportContext && effectiveInvoiceCae?.trim()
      ? {
          ...exportContext,
          cae: effectiveInvoiceCae,
          issuedAt: effectiveInvoiceIssuedAt,
          qrUrl: effectiveInvoiceQrUrl,
          // El retry en-sesión trae numeración/CAE/QR frescos — mergearlos para
          // que PDF e impresión reflejen la emisión sin recargar el detalle.
          caeExpiration: emitOverride?.caeExpiration ?? exportContext.caeExpiration,
          invoiceType: emitOverride?.invoiceType ?? exportContext.invoiceType,
          pointOfSale: emitOverride?.pointOfSale ?? exportContext.pointOfSale,
          invoiceNumber: emitOverride?.invoiceNumber ?? exportContext.invoiceNumber,
          netAmount: emitOverride?.netAmount ?? exportContext.netAmount,
          ivaAmount: emitOverride?.ivaAmount ?? exportContext.ivaAmount,
          buyerName: emitOverride?.buyerName ?? exportContext.buyerName,
          buyerCuit: emitOverride?.buyerCuit ?? exportContext.buyerCuit,
        }
      : null;

  if (fiscalMode === 'non_fiscal') return null;

  const fiscalUi = resolveFiscalEmissionUi({
    fiscalMode,
    fiscalStatus: effectiveFiscalStatus,
    invoiceStatus: effectiveInvoiceStatus,
    invoiceCae: effectiveInvoiceCae,
    invoiceLastError,
  });
  const statusLabel = fiscalUi.displayLabel;
  const persistedArcaError = fiscalUi.userErrorMessage;
  const showArcaErrorBanner = Boolean(persistedArcaError && !effectiveInvoiceCae?.trim());
  const caeLine = formatInvoiceCaeLine(effectiveInvoiceCae, effectiveInvoiceIssuedAt);
  const showRetry =
    canRetry &&
    saleStatus !== 'voided' &&
    (effectiveFiscalStatus === 'pending_sync' ||
      effectiveInvoiceStatus === 'pending' ||
      effectiveInvoiceStatus === 'pending_retry' ||
      effectiveFiscalStatus === 'pending_retry' ||
      !effectiveInvoiceCae?.trim());

  const showNcRetry =
    canRetry &&
    saleStatus === 'voided' &&
    (creditNoteStatus === 'pending' ||
      creditNoteStatus === 'pending_retry' ||
      creditNoteStatus === 'error') &&
    !effectiveCreditNoteCae?.trim();

  const canExport = Boolean(exportInput);
  const qrVerificationUrl = buildAfipQrUrl(
    exportContext?.qrPayload ?? null,
    effectiveInvoiceQrUrl,
    {
      buyerCuit: exportContext?.buyerCuit ?? null,
      cae: effectiveInvoiceCae,
    }
  );
  const isHomologacion =
    emitterFiscalEnvironment?.trim().toLowerCase() === 'homologacion' ||
    emitterFiscalEnvironment?.trim().toLowerCase() === 'homologación';
  const canConstatar = Boolean(effectiveInvoiceCae?.trim());

  const runExport = async (action: 'downloadPdf' | 'copy') => {
    if (!exportInput) return;
    setExportBusy(true);
    try {
      if (action === 'downloadPdf') await downloadFiscalComprobantePdf(exportInput);
      else await copyFiscalComprobanteSummary(exportInput);
    } catch (err: unknown) {
      setRetryError(
        err instanceof Error ? err.message : FISCAL_SYSTEM_COPY.panelErrors.exportFailed,
      );
    } finally {
      setExportBusy(false);
    }
  };

  const handleOpenQr = () => {
    if (qrVerificationUrl) void Linking.openURL(qrVerificationUrl);
  };

  const handleConsultAfip = async () => {
    setConsulting(true);
    setRetryError(null);
    try {
      const result = await consultFiscalInvoiceInAfip(saleId, branchId);
      showInfo(FISCAL_SYSTEM_COPY.panel.constatacionTitle, formatConsultSummary(result));
    } catch (err: unknown) {
      setRetryError(
        err instanceof Error ? err.message : FISCAL_SYSTEM_COPY.panelErrors.consultFailed,
      );
    } finally {
      setConsulting(false);
    }
  };

  const handleRetry = async () => {
    setRetrying(true);
    setRetryError(null);
    try {
      const result = await retryFiscalInvoiceEmit(saleId, branchId);
      setEmitOverride(result);
      onRetryComplete?.();
    } catch (err: unknown) {
      setRetryError(
        err instanceof Error ? err.message : FISCAL_SYSTEM_COPY.panelErrors.retryFailed,
      );
    } finally {
      setRetrying(false);
    }
  };

  const persistBuyerDraft = async (retryAfter: boolean) => {
    setBuyerSaving(true);
    setRetryError(null);
    try {
      await updateSaleFiscalBuyerForReemit({
        saleId,
        consumidorFinal: buyerDraft.consumidorFinal,
        buyerCuit: buyerDraft.consumidorFinal ? null : normalizeCuitDigits(buyerDraft.cuit),
        buyerName: buyerDraft.consumidorFinal ? 'Consumidor Final' : buyerDraft.razonSocial?.trim(),
        buyerTaxCondition: buyerDraft.consumidorFinal ? 5 : buyerDraft.taxCondition,
        fiscalInvoiceType: exportContext?.invoiceType ?? null,
      });
      setBuyerEditOpen(false);
      showSuccess(
        FISCAL_SYSTEM_COPY.panel.buyerSaved,
        retryAfter ? FISCAL_SYSTEM_COPY.panel.buyerSavedRetry : undefined
      );
      onRetryComplete?.();
      if (retryAfter) {
        setRetrying(true);
        try {
          const result = await retryFiscalInvoiceEmit(saleId, branchId);
          setEmitOverride(result);
          onRetryComplete?.();
        } catch (retryErr: unknown) {
          setRetryError(
            retryErr instanceof Error ? retryErr.message : FISCAL_SYSTEM_COPY.panelErrors.retryFailed
          );
        } finally {
          setRetrying(false);
        }
      }
    } catch (err: unknown) {
      setRetryError(
        err instanceof Error ? err.message : FISCAL_SYSTEM_COPY.panelErrors.buyerUpdateFailed,
      );
    } finally {
      setBuyerSaving(false);
    }
  };

  /** Emisor del perfil fiscal (datos que AFIP pide en el comprobante impreso). */
  const loadEmitterInput = async () => {
    const ctx = await getFiscalContext(branchId).catch(() => null);
    const profile = ctx?.profile;
    return {
      emitter: {
        businessName: profile?.businessName ?? exportContext?.emitterBusinessName,
        cuit: profile?.emitterCuit ?? exportContext?.emitterCuit,
        taxRegime: profile?.taxRegime ?? null,
        domicilioComercial: profile?.domicilioComercial,
        iibb: profile?.iibb,
        inicioActividades: profile?.inicioActividades,
      },
      environment: profile?.environment ?? emitterFiscalEnvironment ?? null,
    };
  };

  /** Imprime la Factura A/B/C por la térmica (CAE + QR AFIP). */
  const handlePrintThermal = async () => {
    if (!exportInput) return;
    setThermalPrinting(true);
    setRetryError(null);
    try {
      const invoice = await fetchFiscalInvoiceForSale(saleId);
      const { emitter, environment } = await loadEmitterInput();
      let trackingUrl: string | null = null;
      try {
        trackingUrl = await fetchSalePickupTrackingUrl(saleId);
      } catch {
        trackingUrl = null;
      }
      await printFiscalComprobante({
        branchId,
        input: {
          invoiceType: invoice?.invoiceType ?? exportInput.invoiceType,
          pointOfSale: invoice?.pointOfSale ?? exportInput.pointOfSale,
          invoiceNumber: invoice?.invoiceNumber ?? exportInput.invoiceNumber,
          cae: effectiveInvoiceCae?.trim() ?? '',
          caeExpiration: invoice?.caeExpiration ?? exportInput.caeExpiration,
          issuedAt: invoice?.issuedAt ?? exportInput.issuedAt,
          qrUrl: invoice ? null : effectiveInvoiceQrUrl,
          qrPayload: invoice?.qrPayload ?? exportContext?.qrPayload,
          buyerName: invoice?.buyerName ?? exportInput.buyerName,
          buyerCuit: invoice?.buyerCuit ?? exportInput.buyerCuit,
          buyerTaxCondition: invoice?.buyerTaxCondition,
          items: exportInput.items,
          total: invoice?.totalAmount ?? exportInput.saleTotal,
          netAmount: invoice?.netAmount ?? exportInput.netAmount,
          ivaAmount: invoice?.ivaAmount ?? exportInput.ivaAmount,
          saleRef: exportInput.saleRef,
          diningTableName: exportInput.diningTableName,
          copyLabel: 'DUPLICADO',
          salePaymentConditionLabel: fiscalSalePaymentConditionLabel(
            exportInput.paymentMethodIds ?? [],
          ),
          trackingUrl,
          emitter,
          environment,
        },
      });
    } catch (err: unknown) {
      setRetryError(
        err instanceof Error ? err.message : FISCAL_SYSTEM_COPY.panelErrors.printFailed,
      );
    } finally {
      setThermalPrinting(false);
    }
  };

  /** Imprime la Nota de crédito emitida por la térmica. */
  const handlePrintCreditNote = async () => {
    setNcPrinting(true);
    setRetryError(null);
    try {
      const nc = await fetchCreditNoteForSale(saleId);
      const ncCae = effectiveCreditNoteCae?.trim() || nc?.cae?.trim() || '';
      if (!nc?.cae && !ncCae) {
        throw new Error(FISCAL_SYSTEM_COPY.creditNote.notIssuedYet);
      }
      const { emitter, environment } = await loadEmitterInput();
      const originalNro = formatComprobanteNumber(
        exportContext?.pointOfSale,
        exportContext?.invoiceNumber
      );
      const originalLabel = exportContext?.invoiceType
        ? spanishInvoiceTypeLabel(exportContext.invoiceType).toUpperCase()
        : null;
      await printFiscalComprobante({
        branchId,
        input: {
          invoiceType:
            nc?.invoiceType ??
            resolveInvoiceType({
              emitterRegime:
                emitter.taxRegime === 'responsable_inscripto'
                  ? 'responsable_inscripto'
                  : 'monotributo',
              isCreditNote: true,
              originalInvoiceType: exportContext?.invoiceType,
            }),
          pointOfSale: nc?.pointOfSale,
          invoiceNumber: nc?.invoiceNumber,
          cae: nc?.cae ?? ncCae,
          caeExpiration: nc?.caeExpiration,
          issuedAt: nc?.issuedAt ?? creditNoteIssuedAt,
          qrPayload: nc?.qrPayload,
          buyerName: nc?.buyerName ?? exportContext?.buyerName,
          buyerCuit: nc?.buyerCuit ?? exportContext?.buyerCuit,
          buyerTaxCondition: nc?.buyerTaxCondition,
          items: exportContext?.items ?? [],
          total: nc?.totalAmount ?? exportContext?.saleTotal ?? 0,
          netAmount: nc?.netAmount,
          ivaAmount: nc?.ivaAmount,
          saleRef: exportContext?.saleRef,
          copyLabel: 'DUPLICADO',
          relatedDocLabel:
            originalLabel && originalNro ? `Anula ${originalLabel} ${originalNro}` : null,
          emitter,
          environment,
        },
      });
    } catch (err: unknown) {
      setRetryError(
        err instanceof Error ? err.message : FISCAL_SYSTEM_COPY.creditNote.printFailed,
      );
    } finally {
      setNcPrinting(false);
    }
  };

  const handleRetryCreditNote = async () => {
    setNcRetrying(true);
    setRetryError(null);
    try {
      const result = await retryFiscalCreditNoteEmit(saleId);
      if (result.cae?.trim()) setNcCaeOverride(result.cae.trim());
      onRetryComplete?.();
    } catch (err: unknown) {
      setRetryError(
        err instanceof Error ? err.message : FISCAL_SYSTEM_COPY.creditNote.retryFailed,
      );
    } finally {
      setNcRetrying(false);
    }
  };

  const parseNdAmount = (): number | null => {
    const normalized = ndAmountText.replace(',', '.').trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) && parsed > 0 ? roundMoney(parsed, moneyScale) : null;
  };

  const handleEmitDebitNote = async () => {
    const amount = parseNdAmount();
    if (amount == null) {
      setNdError(FISCAL_SYSTEM_COPY.debitNote.amountInvalid);
      return;
    }
    setNdEmitting(true);
    setNdError(null);
    try {
      const pendingNd = debitNotes.find((n) => n.status === 'pending_retry');
      const result = await emitFiscalDebitNote(saleId, amount, null, pendingNd?.id ?? null);
      if (result.error) {
        setNdError(
          result.status === 'pending_retry'
            ? FISCAL_SYSTEM_COPY.debitNote.emitPending
            : result.error
        );
        await reloadDebitNotes();
        return;
      }
      showSuccess(
        FISCAL_SYSTEM_COPY.debitNote.emitSuccessTitle,
        FISCAL_SYSTEM_COPY.debitNote.emitSuccessBody,
      );
      setNdAmountText('');
      setNdFormOpen(false);
      await reloadDebitNotes();
      onRetryComplete?.();
    } catch (err: unknown) {
      setNdError(
        err instanceof Error ? err.message : FISCAL_SYSTEM_COPY.debitNote.emitFailed,
      );
      await reloadDebitNotes().catch(() => undefined);
    } finally {
      setNdEmitting(false);
    }
  };

  /** Imprime una Nota de débito emitida por la térmica. */
  const handlePrintDebitNote = async (nd: FiscalInvoiceDetailRow) => {
    setNdPrintingId(nd.id);
    setNdError(null);
    try {
      const { emitter, environment } = await loadEmitterInput();
      const originalNro = formatComprobanteNumber(
        exportContext?.pointOfSale,
        exportContext?.invoiceNumber
      );
      const originalLabel = exportContext?.invoiceType
        ? spanishInvoiceTypeLabel(exportContext.invoiceType).toUpperCase()
        : null;
      await printFiscalComprobante({
        branchId,
        input: {
          invoiceType: nd.invoiceType,
          pointOfSale: nd.pointOfSale,
          invoiceNumber: nd.invoiceNumber,
          cae: nd.cae ?? '',
          caeExpiration: nd.caeExpiration,
          issuedAt: nd.issuedAt,
          qrPayload: nd.qrPayload,
          buyerName: nd.buyerName ?? exportContext?.buyerName,
          buyerCuit: nd.buyerCuit ?? exportContext?.buyerCuit,
          buyerTaxCondition: nd.buyerTaxCondition,
          items: [],
          total: nd.totalAmount ?? 0,
          netAmount: nd.netAmount,
          ivaAmount: nd.ivaAmount,
          saleRef: exportContext?.saleRef,
          copyLabel: 'DUPLICADO',
          relatedDocLabel:
            originalLabel && originalNro ? `Ref ${originalLabel} ${originalNro}` : null,
          emitter,
          environment,
        },
      });
    } catch (err: unknown) {
      setNdError(
        err instanceof Error ? err.message : FISCAL_SYSTEM_COPY.debitNote.printFailed,
      );
    } finally {
      setNdPrintingId(null);
    }
  };

  const ncStatusLabel =
    effectiveCreditNoteCae?.trim()
      ? FISCAL_SYSTEM_COPY.creditNote.issued
      : creditNoteStatus === 'pending' || creditNoteStatus === 'pending_retry'
        ? FISCAL_SYSTEM_COPY.creditNote.pending
        : creditNoteStatus === 'error'
          ? FISCAL_SYSTEM_COPY.creditNote.error
          : creditNoteStatus === 'skipped'
            ? FISCAL_SYSTEM_COPY.creditNote.notRequired
            : null;

  return (
    <>
      <Text style={styles.sectionTitle}>{FISCAL_SYSTEM_COPY.panel.sectionTitle}</Text>
      <View style={styles.saleDetailSection}>
        <View style={styles.fiscalComprobanteRow}>
          <Ionicons
            name={fiscalUi.tone === 'danger' ? 'alert-circle-outline' : 'receipt-outline'}
            size={20}
            color={fiscalUi.tone === 'danger' ? colors.danger : colors.accent}
          />
          <Text
            style={[
              styles.fiscalComprobanteStatus,
              fiscalUi.tone === 'danger' ? { color: colors.danger } : null,
            ]}
          >
            {statusLabel}
          </Text>
        </View>
        {showArcaErrorBanner ? (
          <View style={styles.voidedBanner}>
            <Text style={styles.voidedBannerText}>{persistedArcaError}</Text>
            {fiscalUi.isDataRejection ? (
              <Text style={styles.voidedBannerText}>{FISCAL_SYSTEM_COPY.errors.padronHint}</Text>
            ) : null}
          </View>
        ) : null}
        {canEditBuyer && !buyerEditOpen ? (
          <TouchableOpacity
            style={styles.fiscalActionBtn}
            onPress={() => setBuyerEditOpen(true)}
            accessibilityRole="button"
            testID="fiscal.comprobante.edit-buyer"
          >
            <Ionicons name="create-outline" size={18} color={colors.accent} />
            <Text style={styles.fiscalActionText}>{FISCAL_SYSTEM_COPY.panel.editBuyer}</Text>
          </TouchableOpacity>
        ) : null}
        {canEditBuyer && buyerEditOpen ? (
          <View style={{ marginTop: spacing.sm }}>
            <PosFiscalBuyerSection
              branchId={branchId}
              draft={buyerDraft}
              onDraftChange={(patch) => setBuyerDraft((d) => ({ ...d, ...patch }))}
              disabled={buyerSaving || retrying}
            />
            <View style={styles.fiscalComprobanteActions}>
              <TouchableOpacity
                style={styles.fiscalActionBtn}
                onPress={() => void persistBuyerDraft(false)}
                disabled={buyerSaving}
                accessibilityRole="button"
              >
                {buyerSaving ? (
                  <ActivityIndicator size="small" color={colors.accent} />
                ) : (
                  <Text style={styles.fiscalActionText}>{FISCAL_SYSTEM_COPY.panel.saveBuyer}</Text>
                )}
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.fiscalActionBtn}
                onPress={() => void persistBuyerDraft(true)}
                disabled={buyerSaving || retrying}
                accessibilityRole="button"
              >
                <Text style={styles.fiscalActionText}>{FISCAL_SYSTEM_COPY.panel.saveAndRetry}</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : null}
        {caeLine ? <Text style={styles.fiscalComprobanteCae}>{caeLine}</Text> : null}
        {isHomologacion && effectiveInvoiceCae?.trim() ? (
          <View style={styles.customerBanner}>
            <Text style={styles.customerBannerText}>
              {FISCAL_SYSTEM_COPY.panel.homologacionBanner}
            </Text>
          </View>
        ) : null}
        <View style={styles.fiscalComprobanteActions}>
          {!isHomologacion && qrVerificationUrl ? (
            <TouchableOpacity style={styles.fiscalActionBtn} onPress={handleOpenQr} accessibilityRole="link" testID="fiscal.comprobante.qr-afip">
              <Ionicons name="qr-code-outline" size={18} color={colors.accent} />
              <Text style={styles.fiscalActionText}>{FISCAL_SYSTEM_COPY.panel.viewQrArca}</Text>
            </TouchableOpacity>
          ) : null}
          {canConstatar ? (
            <TouchableOpacity
              style={styles.fiscalActionBtn}
              onPress={() => void handleConsultAfip()}
              disabled={consulting}
              accessibilityRole="button"
            >
              {consulting ? (
                <ActivityIndicator size="small" color={colors.accent} />
              ) : (
                <>
                  <Ionicons name="shield-checkmark-outline" size={18} color={colors.accent} />
                  <Text style={styles.fiscalActionText}>
                    {isHomologacion
                      ? FISCAL_SYSTEM_COPY.panel.constatarArcaHomologacion
                      : FISCAL_SYSTEM_COPY.panel.constatarArca}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          ) : null}
          {canExport ? (
            <TouchableOpacity
              style={styles.fiscalActionBtn}
              onPress={() => void handlePrintThermal()}
              disabled={thermalPrinting}
              accessibilityRole="button"
              testID="fiscal.comprobante.print-thermal"
            >
              {thermalPrinting ? (
                <ActivityIndicator size="small" color={colors.accent} />
              ) : (
                <>
                  <Ionicons name="print-outline" size={18} color={colors.accent} />
                  <Text style={styles.fiscalActionText}>{FISCAL_SYSTEM_COPY.panel.printThermal}</Text>
                </>
              )}
            </TouchableOpacity>
          ) : null}
          {canExport ? (
            <>
              <TouchableOpacity
                style={styles.fiscalActionBtn}
                onPress={() => void runExport('downloadPdf')}
                disabled={exportBusy}
                accessibilityRole="button"
                testID="fiscal.comprobante.download-pdf"
              >
                {exportBusy ? (
                  <ActivityIndicator size="small" color={colors.accent} />
                ) : (
                  <>
                    <Ionicons name="download-outline" size={18} color={colors.accent} />
                    <Text style={styles.fiscalActionText}>{FISCAL_SYSTEM_COPY.panel.downloadPdf}</Text>
                  </>
                )}
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.fiscalActionBtn}
                onPress={() => void runExport('copy')}
                disabled={exportBusy}
                accessibilityRole="button"
              >
                <Ionicons name="copy-outline" size={18} color={colors.textPrimary} />
                <Text style={styles.fiscalActionText}>{FISCAL_SYSTEM_COPY.panel.copySummary}</Text>
              </TouchableOpacity>
            </>
          ) : null}
          {canExport && Platform.OS !== 'web' ? (
            <Text style={styles.caption}>
              {FISCAL_SYSTEM_COPY.panel.mobilePdfHint}
            </Text>
          ) : null}
          {showRetry ? (
            <TouchableOpacity
              style={styles.fiscalActionBtn}
              onPress={() => void handleRetry()}
              disabled={retrying}
              accessibilityRole="button"
            >
              {retrying ? (
                <ActivityIndicator size="small" color={colors.accent} />
              ) : (
                <>
                  <Ionicons name="refresh-outline" size={18} color={colors.accent} />
                  <Text style={styles.fiscalActionText}>{FISCAL_SYSTEM_COPY.panel.retryEmission}</Text>
                </>
              )}
            </TouchableOpacity>
          ) : null}
        </View>
        {retryError ? <Text style={[styles.caption, { color: colors.danger, marginTop: spacing.xs }]}>{retryError}</Text> : null}
      </View>

      {saleStatus === 'voided' && ncStatusLabel ? (
        <>
          <Text style={styles.sectionTitle}>Nota de crédito</Text>
          <View style={styles.saleDetailSection}>
            <Text style={styles.fiscalComprobanteStatus}>{ncStatusLabel}</Text>
            {effectiveCreditNoteCae?.trim() ? (
              <Text style={styles.fiscalComprobanteCae}>
                {formatInvoiceCaeLine(effectiveCreditNoteCae, creditNoteIssuedAt)}
              </Text>
            ) : null}
            {effectiveCreditNoteCae?.trim() ? (
              <TouchableOpacity
                style={styles.fiscalActionBtn}
                onPress={() => void handlePrintCreditNote()}
                disabled={ncPrinting}
                accessibilityRole="button"
                testID="fiscal.credit-note.print-thermal"
              >
                {ncPrinting ? (
                  <ActivityIndicator size="small" color={colors.accent} />
                ) : (
                  <>
                    <Ionicons name="print-outline" size={18} color={colors.accent} />
                    <Text style={styles.fiscalActionText}>Imprimir NC</Text>
                  </>
                )}
              </TouchableOpacity>
            ) : null}
            {showNcRetry ? (
              <TouchableOpacity
                style={styles.fiscalActionBtn}
                onPress={() => void handleRetryCreditNote()}
                disabled={ncRetrying}
                accessibilityRole="button"
              >
                {ncRetrying ? (
                  <ActivityIndicator size="small" color={colors.accent} />
                ) : (
                  <>
                    <Ionicons name="refresh-outline" size={18} color={colors.accent} />
                    <Text style={styles.fiscalActionText}>
                      {FISCAL_SYSTEM_COPY.creditNote.retryEmission}
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            ) : null}
          </View>
        </>
      ) : null}

      {canEmitDebitNote ? (
        <>
          <Text style={styles.sectionTitle}>Nota de débito</Text>
          <View style={styles.saleDetailSection}>
            {debitNotes.map((nd) => (
              <View key={nd.id} style={styles.fiscalComprobanteRow}>
                <Ionicons name="document-outline" size={18} color={colors.accent} />
                <Text style={styles.fiscalComprobanteStatus}>
                  {spanishInvoiceTypeLabel(nd.invoiceType)}{' '}
                  {nd.invoiceNumber != null
                    ? formatComprobanteNumber(nd.pointOfSale, nd.invoiceNumber)
                    : ''}
                  {nd.totalAmount != null ? ` · ${formatMoney(nd.totalAmount)}` : ''}
                </Text>
                {nd.cae?.trim() ? (
                  <TouchableOpacity
                    style={styles.fiscalActionBtn}
                    onPress={() => void handlePrintDebitNote(nd)}
                    disabled={ndPrintingId === nd.id}
                    accessibilityRole="button"
                  >
                    {ndPrintingId === nd.id ? (
                      <ActivityIndicator size="small" color={colors.accent} />
                    ) : (
                      <Ionicons name="print-outline" size={18} color={colors.accent} />
                    )}
                  </TouchableOpacity>
                ) : (
                  <Text style={styles.caption}>Pendiente</Text>
                )}
              </View>
            ))}
            {ndFormOpen ? (
              <View style={{ gap: spacing.xs }}>
                <Text style={styles.caption}>
                  Monto a debitar del comprobante {formatComprobanteNumber(
                    exportContext?.pointOfSale,
                    exportContext?.invoiceNumber
                  )}
                </Text>
                <NumericTextInput
                  kind="decimalMoney"
                  style={[settingsStyles.input, { minHeight: 44 }]}
                  value={ndAmountText}
                  onChangeText={setNdAmountText}
                  placeholder={FISCAL_SYSTEM_COPY.debitNote.amountPlaceholder}
                  placeholderTextColor={colors.textSecondary}
                  testID="fiscal.debit-note.amount"
                />
                <View style={{ flexDirection: 'row', gap: spacing.xs }}>
                  <TouchableOpacity
                    style={styles.fiscalActionBtn}
                    onPress={() => void handleEmitDebitNote()}
                    disabled={ndEmitting}
                    accessibilityRole="button"
                    testID="fiscal.debit-note.emit"
                  >
                    {ndEmitting ? (
                      <ActivityIndicator size="small" color={colors.accent} />
                    ) : (
                      <>
                        <Ionicons name="checkmark-circle-outline" size={18} color={colors.accent} />
                        <Text style={styles.fiscalActionText}>Emitir ND</Text>
                      </>
                    )}
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.fiscalActionBtn}
                    onPress={() => {
                      setNdFormOpen(false);
                      setNdError(null);
                    }}
                    disabled={ndEmitting}
                    accessibilityRole="button"
                  >
                    <Ionicons name="close-outline" size={18} color={colors.textPrimary} />
                    <Text style={styles.fiscalActionText}>Cancelar</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              <TouchableOpacity
                style={styles.fiscalActionBtn}
                onPress={() => setNdFormOpen(true)}
                accessibilityRole="button"
                testID="fiscal.debit-note.open"
              >
                <Ionicons name="add-circle-outline" size={18} color={colors.accent} />
                <Text style={styles.fiscalActionText}>Emitir nota de débito</Text>
              </TouchableOpacity>
            )}
            {ndError ? (
              <Text style={[styles.caption, { color: colors.danger, marginTop: spacing.xs }]}>
                {ndError}
              </Text>
            ) : null}
          </View>
        </>
      ) : null}
    </>
  );
}
